// Тему встраиваемого виджета диктует САЙТ, а не операционка гостя: гость с
// тёмной macOS на светлом сайте клиента получал тёмный виджет — чужеродный
// прямоугольник поверх светлой страницы. Поэтому prefers-color-scheme из
// цепочки выбора убран, а фолбэк — светлая тема (большинство сайтов светлые).
// Модуль чистый (никакого DOM) — значения виджет читает сам и передаёт сюда.

export type WidgetTheme = 'light' | 'dark';

/** Настройка владельца (кабинет → Настройки → Виджет): «авто» = по сайту. */
export type ThemePreference = 'auto' | WidgetTheme;

export interface ThemeSignals {
  /** Настройка владельца из конфига виджета — абсолютный приоритет. */
  preference?: string | null;
  /** `<html data-theme>` хост-страницы: явный сигнал сайта (в т.ч. SPA-переключатель). */
  hostTheme?: string | null;
  /** Фон хоста от ближнего слоя к дальнему: body, затем documentElement —
   *  прозрачный body пропускает фон документа. */
  backgrounds?: Array<string | null | undefined>;
}

/** Каналы 0–255; Rgba — плюс альфа 0–1. */
type Rgb = [number, number, number];
type Rgba = [number, number, number, number];

/** Порог «фон тёмный» по относительной яркости WCAG 2.1. 0.179 — точка равного
 *  контраста фона с белым и чёрным текстом: (L+0.05)² = 1.05·0.05 → L ≈ 0.1791.
 *  Ниже неё на фоне читается светлый текст (сайт тёмный), выше — тёмный. */
const DARK_LUMINANCE_MAX = 0.179;
/** Холст страницы под всеми слоями фона: браузер красит его белым. */
const PAGE_CANVAS: Rgb = [255, 255, 255];

/**
 * Тема виджета по цепочке приоритетов (сверху вниз):
 * 1) настройка владельца light/dark; 2) `<html data-theme>` хоста;
 * 3) авто-определение по яркости фона сайта; 4) светлая.
 */
export function resolveWidgetTheme({ preference, hostTheme, backgrounds = [] }: ThemeSignals): WidgetTheme {
  return explicitTheme(preference) ?? explicitTheme(hostTheme) ?? backgroundTheme(backgrounds) ?? 'light';
}

/** 'light'/'dark' — тема, всё прочее ('auto', пусто, мусор) — нет сигнала. */
function explicitTheme(value?: string | null): WidgetTheme | null {
  return value === 'light' || value === 'dark' ? value : null;
}

/** Тема по эффективному фону страницы: слои накладываем на белый холст от
 *  дальнего к ближнему и смотрим яркость результата. Полупрозрачный слой фон
 *  не «закрашивает» — сквозь rgba(0,0,0,.2) виден светлый низ, и сайт остаётся
 *  светлым. Ни один слой не прочитан — сигнала нет. */
function backgroundTheme(colors: Array<string | null | undefined>): WidgetTheme | null {
  const layers = colors.map(parseRgba).filter((layer): layer is Rgba => layer !== null);
  if (layers.length === 0) return null;

  const paint = layers.reduceRight(blend, PAGE_CANVAS);
  return luminance(paint) <= DARK_LUMINANCE_MAX ? 'dark' : 'light';
}

/** Слой поверх подложки по альфе (source-over). */
function blend(base: Rgb, [red, green, blue, alpha]: Rgba): Rgb {
  return [
    base[0] + (red - base[0]) * alpha,
    base[1] + (green - base[1]) * alpha,
    base[2] + (blue - base[2]) * alpha,
  ];
}

/** rgb()/rgba() → каналы 0–255 + альфа 0–1. Незнакомый синтаксис — null: слой
 *  просто не участвует (современные oklch/color(srgb …) виджет переводит в
 *  rgb() до вызова — ChatWidget#normalizeColor). */
function parseRgba(color?: string | null): Rgba | null {
  const inner = /^rgba?\(([^)]+)\)$/.exec((color ?? '').trim())?.[1];
  const parts = inner
    ?.split(/[\s,/]+/)
    .filter(Boolean)
    .map(Number);
  if (!parts || parts.length < 3) return null;

  const [red, green, blue] = parts;
  const alpha = parts.length > 3 ? parts[3] : 1;
  if ([red, green, blue, alpha].some(Number.isNaN)) return null;

  return [red, green, blue, Math.min(1, Math.max(0, alpha))];
}

/** Относительная яркость WCAG 2.1 (sRGB → линейное пространство). */
function luminance([red, green, blue]: Rgb): number {
  const [r, g, b] = [red, green, blue].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
